import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { isEnglish, plainText } from '../lib/english';
import { useI18n } from '../lib/i18n';
import { FormGrid, FormPage } from '../components/FormPage';
import { LogoPicker, type LogoValue } from '../components/LogoPicker';
import { Checkbox, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { useCountries, useCurrencies } from './Clients';

/**
 * CR-001 §2–§5 — creating and editing a client company, as a page.
 *
 * Routes: `/clients/new` and `/clients/:id/edit`.
 */
export function ClientFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const countries = useCountries();
  const currencies = useCurrencies();

  const existing = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<any>(`/clients/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    companyName: '',
    companyNameAr: '',
    legalName: '',
    code: '',
    countryCode: 'EG',
    defaultCurrency: 'EGP',
    address: '',
    taxNumber: '',
    registrationNumber: '',
    website: '',
    notes: '',
  });
  // The logo is uploaded as soon as it is picked; saving the form says which one
  // the client has. Sent only once it changed, as null when it was taken off.
  const [logo, setLogo] = useState<LogoValue | null>(null);
  const [logoChanged, setLogoChanged] = useState(false);

  // A new client is offered the last code + 1 (operator, 2026-10-07): asked for
  // afresh on every visit, and filled in once, only into an empty field — it is
  // a reminder of where the codes are up to, and can be changed like any field.
  const nextCode = useQuery({
    queryKey: ['clients', 'next-code'],
    queryFn: () => api.get<{ code: string; after: string | null }>('/clients/next-code'),
    enabled: !editing,
    gcTime: 0,
  });
  const [offered, setOffered] = useState(false);
  if (!editing && nextCode.data && !offered) {
    setOffered(true);
    if (!form.code) setForm({ ...form, code: nextCode.data.code });
  }

  // Seed the form once the record arrives; `loaded` keeps later edits intact.
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    setForm({
      companyName: existing.data.companyName ?? '',
      companyNameAr: existing.data.companyNameAr ?? '',
      legalName: existing.data.legalName ?? '',
      code: existing.data.code ?? '',
      countryCode: existing.data.countryCode ?? 'EG',
      defaultCurrency: existing.data.defaultCurrency ?? 'EGP',
      address: existing.data.address ?? '',
      taxNumber: existing.data.taxNumber ?? '',
      registrationNumber: existing.data.registrationNumber ?? '',
      website: existing.data.website ?? '',
      notes: existing.data.notes ?? '',
    });
    setLogo(existing.data.logo ?? null);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        companyNameAr: form.companyNameAr || undefined,
        legalName: form.legalName || undefined,
        address: form.address || undefined,
        taxNumber: form.taxNumber || undefined,
        registrationNumber: form.registrationNumber || undefined,
        website: form.website || undefined,
        notes: form.notes || undefined,
        // Blank, a new client gets the next code and an edited one keeps its own.
        code: form.code.trim() || undefined,
        ...(logoChanged ? { logoFileId: logo?.id ?? null } : {}),
      };
      return editing ? api.patch<any>(`/clients/${id}`, body) : api.post<any>('/clients', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['client', saved.id] });
      void queryClient.invalidateQueries({ queryKey: ['client-options'] });
      navigate(`/clients/${saved.id}`);
    },
    onError: (e: any) => {
      if (e?.code === 'DUPLICATE_RESOURCE' && e?.details?.field === 'code' && form.code.trim()) {
        return toast.error(t('clients.codeTaken', { code: form.code.trim() }));
      }
      if (e?.details?.field === 'logoFileId' || e?.code === 'FILE_NOT_ATTACHABLE') {
        return toast.error(t('clients.logoRefused', { reason: e.message }));
      }
      toast.error(e.message);
    },
  });

  const codeHint = editing
    ? t('clients.codeEditHint')
    : nextCode.data?.after
      ? t('clients.codeFollows', { code: nextCode.data.after })
      : nextCode.data
        ? t('clients.codeFirst')
        : t('clients.codeHint');

  return (
    <FormPage
      title={editing ? `${t('common.edit')} — ${form.companyName}` : t('clients.new')}
      backTo={editing ? `/clients/${id}` : '/clients'}
      backLabel={editing ? form.companyName || t('clients.title') : t('clients.title')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!form.companyName || !form.countryCode || !form.defaultCurrency}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('clients.companyName')} required>
          <Input
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            autoFocus
          />
        </Field>
        <Field label={t('clients.companyNameAr')}>
          <Input
            dir="rtl"
            value={form.companyNameAr}
            onChange={(e) => setForm({ ...form, companyNameAr: e.target.value })}
          />
        </Field>
        <Field label={t('clients.legalName')}>
          <Input value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} />
        </Field>
        <Field label={t('clients.code')} hint={codeHint}>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            // What a blank code saves as: the next one, or the code it has.
            placeholder={editing ? existing.data?.code : nextCode.data?.code}
            maxLength={40}
            dir="ltr"
          />
        </Field>

        <Field label={t('clients.country')} required>
          <Select
            value={form.countryCode}
            onChange={(e) => {
              const country = (countries.data ?? []).find((c) => c.code === e.target.value);
              setForm({
                ...form,
                countryCode: e.target.value,
                // A hint, not a rule: §5 says the currency is chosen. Pre-filling
                // the usual one still leaves it editable right below.
                defaultCurrency: country?.defaultCurrency ?? form.defaultCurrency,
              });
            }}
          >
            {(countries.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{localized(c)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('clients.currency')} required hint={t('clients.currencyHint')}>
          <Select
            value={form.defaultCurrency}
            onChange={(e) => setForm({ ...form, defaultCurrency: e.target.value })}
          >
            {(currencies.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {locale === 'ar' && c.nameAr ? c.nameAr : c.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('clients.taxNumber')}>
          <Input value={form.taxNumber} onChange={(e) => setForm({ ...form, taxNumber: e.target.value })} />
        </Field>
        <Field label={t('clients.registrationNumber')}>
          <Input
            value={form.registrationNumber}
            onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
          />
        </Field>
        <Field label={t('clients.website')} className="sm:col-span-2">
          <Input
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder="https://"
            dir="ltr"
          />
        </Field>
        <div className="sm:col-span-2">
          <p className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{t('clients.logo')}</p>
          <LogoPicker
            value={logo}
            clientName={form.companyName || t('clients.title')}
            onChange={(next) => {
              setLogo(next);
              setLogoChanged(true);
            }}
          />
        </div>
        <Field label={t('clients.address')} className="sm:col-span-2">
          <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/**
 * §2 — a contact person belongs to a client and is never a client themselves.
 * Since 2026-10-12 every field is in English (`lib/english.ts`), as on the
 * admin's form.
 *
 * Routes: `/clients/:id/contacts/new` and `/clients/:id/contacts/:contactId/edit`.
 */
export function ClientContactFormPage() {
  const { id = '', contactId } = useParams();
  const editing = !!contactId;
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const client = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<any>(`/clients/${id}`),
  });

  const [form, setForm] = useState({ name: '', jobTitle: '', email: '', phone: '', isPrimary: false });

  const [loaded, setLoaded] = useState(false);
  if (editing && client.data && !loaded) {
    const contact = client.data.contacts.find((c: any) => c.id === contactId);
    if (contact) {
      setLoaded(true);
      setForm({
        name: contact.name ?? '',
        jobTitle: contact.jobTitle ?? '',
        email: contact.email ?? '',
        phone: contact.phone ?? '',
        isPrimary: contact.isPrimary ?? false,
      });
    }
  }
  // Every field in English — a contact saved before with Arabic shows it here until it is rewritten.
  const notEnglish = (value: string) => (isEnglish(value) ? null : t('clients.contactEnglishOnly'));
  const anyNotEnglish = [form.name, form.jobTitle, form.email, form.phone].some((value) => !isEnglish(value));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: plainText(form.name),
        jobTitle: plainText(form.jobTitle) || undefined,
        email: plainText(form.email) || undefined,
        phone: plainText(form.phone) || undefined,
        isPrimary: form.isPrimary,
      };
      return editing
        ? api.patch(`/clients/${id}/contacts/${contactId}`, body)
        : api.post(`/clients/${id}/contacts`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', id] });
      navigate(`/clients/${id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={editing ? t('common.edit') : t('clients.newContact')}
      subtitle={client.data?.companyName}
      backTo={`/clients/${id}`}
      backLabel={client.data?.companyName ?? t('clients.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!form.name || anyNotEnglish}
      loading={client.isLoading}
      error={client.error}
      onRetry={() => void client.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <p className="text-body-sm text-muted sm:col-span-2">{t('clients.contactEnglishNote')}</p>
        <Field label={t('common.name')} required error={notEnglish(form.name)}>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
            dir="ltr"
          />
        </Field>
        <Field label={t('clients.jobTitle')} error={notEnglish(form.jobTitle)}>
          <Input value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} dir="ltr" />
        </Field>
        <Field label={t('common.email')} error={notEnglish(form.email)}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            dir="ltr"
          />
        </Field>
        <Field label={t('common.phone')} error={notEnglish(form.phone)}>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} dir="ltr" />
        </Field>
        <div className="sm:col-span-2">
          <Checkbox
            checked={form.isPrimary}
            onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
            label={t('clients.primaryContact')}
          />
        </div>
      </FormGrid>
    </FormPage>
  );
}
